// src/Contexts/BrandContext.tsx
import React, { createContext, useContext, ReactNode } from 'react';
import { BrandService, Brand } from '../Services/brandService';
import { useAllBrandsQuery } from '../Hooks/useBrandQuery';

interface BrandContextType {
  brands: Brand[];
  allBrands: Brand[];
  activeBrands: Brand[];
  featuredBrands: Brand[];
  loading: boolean;
  error: string | null;
  refreshBrands: () => Promise<void>;
  getBrandById: (id: string) => Promise<Brand | null>;
  getBrandBySlug: (slug: string) => Promise<Brand | null>;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrands = () => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrands must be used within a BrandProvider');
  }
  return context;
};

interface BrandProviderProps {
  children: ReactNode;
}

export const BrandProvider: React.FC<BrandProviderProps> = ({ children }) => {
  const { data: allBrandsData, isLoading, isError, error, refetch } = useAllBrandsQuery();

  // Derive activeBrands and featuredBrands from the single cached result.
  // This eliminates the original 3 parallel Firestore reads on every mount —
  // now only 1 read happens, and all derived lists are computed in memory.
  const brands: Brand[] = allBrandsData ?? [];
  const activeBrands = brands.filter(b => b.isActive);
  const featuredBrands = brands.filter(b => b.isActive && b.featured);

  const refreshBrands = async () => {
    await refetch();
  };

  const getBrandById = async (id: string): Promise<Brand | null> => {
    // Check the cache first before hitting Firestore.
    const cached = brands.find(b => b.id === id);
    if (cached) return cached;
    return BrandService.getBrandById(id);
  };

  const getBrandBySlug = async (slug: string): Promise<Brand | null> => {
    const cached = brands.find(b => b.slug === slug);
    if (cached) return cached;
    return BrandService.getBrandBySlug(slug);
  };

  const value: BrandContextType = {
    brands,
    allBrands: brands,
    activeBrands,
    featuredBrands,
    loading: isLoading,
    error: isError ? (error as Error)?.message ?? 'Failed to load brands' : null,
    refreshBrands,
    getBrandById,
    getBrandBySlug,
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};